<template>
  <scroll-content class="funnel-session">
    <div class="data-pane">
      <div class="header">
        <p class="url ellipsis">{{ url }}</p>
      </div>
      <ul v-if="!isPhoneDevice" class="legend">
        <li v-for="nodeType in ['active', 'inactive', 'hover', 'click', 'hoverClick', 'scrollUp', 'scrollDown']" :key="nodeType"
          :class="NODE_TYPE[nodeType].cssClass">
          <button type="button"
            class="btn-legend"
            :class="{ off: NODE_TYPE[nodeType].isOff }"
            :disabled="NODE_TYPE[nodeType].isDisabled"
            @click="onLegendItemClick(nodeType)">
            <span class="icon">
              <span v-if="nodeType === 'active'">A</span>
              <span v-else-if="nodeType === 'inactive'">I</span>
              <span v-else></span>
            </span>
            <span class="text">
              <template v-if="nodeType === 'hoverClick'">
                Hover<br>-> Click
              </template>
              <template v-else-if="nodeType === 'scrollDown'">
                Scroll<br>Down
              </template>
              <template v-else>
                {{ NODE_TYPE[nodeType].name }}
              </template>
            </span>
          </button>
        </li>
      </ul>
      <div v-else class="view-options">
        <a v-for="mode in ['width', 'height']" :key="mode" href="#"
          :class="getFitModeClass(mode)"
          @click.prevent="onFitModeChange(mode)">
          <i class="icon"
            :class="mode === 'width' ? 'icon--fit-horizontal' : 'icon--fit-vertical'"></i>
        </a>
      </div>
      <div class="view-area-wrap"
        :class="{ collapse: !showTimeline }">
        <funnel-session-desktop v-if="!isPhoneDevice"
          ref="viewAreaBox"
          :NODE_TYPE="NODE_TYPE"
          :containerWidth="containerWidth"
          :containerHeight="containerHeight"
          :imageUrl="imageUrl"
          :activeIndex="activeIndex"
          :activeItem="activeItem"
          :eventItemsCssClassData="eventItemsCssClassData"
          :eventItems="eventItems"
          :nodeItems="nodeItems"
          :formatTime="formatTime"
          :isActionStart="isActionStart"
          @onNodeClick="setActiveItem"/>
        <funnel-session-mobile v-else
          ref="viewAreaBox"
          :NODE_TYPE="NODE_TYPE"
          :containerWidth="containerWidth"
          :containerHeight="containerHeight"
          :imageUrl="imageUrl"
          :activeIndex="activeIndex"
          :activeItem="activeItem"
          :eventItemsCssClassData="eventItemsCssClassData"
          :eventItems="eventItems"
          :nodeItems="nodeItems"
          :isActionStart="isActionStart"
          :fitMode="fitMode"
          :scale="scale"
          :showTimeline="showTimeline"
          :viewportHeight="streamMeta ? streamMeta.ss_h : 0"
          :maxTime="streamMeta ? streamMeta.max_time : 0"
          @onNodeClick="setActiveItem"/>

        <div v-if="sessionLoaderData.isShow" class="session-loader">
          <div class="session-loader-mask"></div>
          <div class="session-loader-content">
            <p class="url ellipsis">{{ this.url }}</p>
            <p class="session-id">{{ streamMeta.ssid }}</p>
            <p class="next">Next</p>
            <p class="next-session-url ellipsis">
              {{ prevNextSession.next ? prevNextSession.next.url : '' }}
            </p>
            <div class="loading">
              <svg class="loading-svg"
                :width="sessionLoaderData.r * 2"
                :height="sessionLoaderData.r * 2">
                <circle class="outer"
                  :cx="sessionLoaderData.r"
                  :cy="sessionLoaderData.r"
                  :r="sessionLoaderData.r"/>
                <circle class="inner"
                  :class="{ play: sessionLoaderData.isPlaying }"
                  :cx="sessionLoaderData.r"
                  :cy="sessionLoaderData.r"
                  :r="sessionLoaderData.r"
                  :style="{
                    'stroke-dasharray': sessionLoaderData.dashArray,
                    'stroke-dashoffset': sessionLoaderData.dashOffset
                  }"
                  @transitionend="onSessionLoaderTransitionEnd"/>
              </svg>
              <button type="button" class="btn-loading"
                @click="onNextSessionPlay">
                <i class="icon play-next"></i>
              </button>
            </div>
            <p class="action">
              <ui-button class="btn-replay"
                @click="onReplay">Replay</ui-button>
              <ui-button class="btn-cancel"
                @click="onNextSessionCancel">Cancel</ui-button>
            </p>
          </div>
        </div>
      </div>
      <div class="control-box">
        <div class="control">
          <ui-button class="control-button prev-next" primary3 size="small"
            :disabled="!prevNextSession.prev"
            @click="onPrevClick">
            {{ $t('funnel.sessionReport.prev') }}
          </ui-button>
          <ui-button class="control-button" circle primary3 size="small"
            :disabled="!hasPrevNode"
            @click="onPrevItemClick">
            <i class="icon icon-play-prev"></i>
          </ui-button>
          <ui-button class="control-button" circle primary3 size="small"
            :disabled="!hasData"
            @click="play">
            <i class="icon icon-play"
              :class="{ 'icon-pause': playState == 'play' }"></i>
          </ui-button>
          <ui-button class="control-button" circle primary3 size="small"
            :disabled="!hasNextNode"
            @click="onNextItemClick">
            <i class="icon icon-play-next"></i>
          </ui-button>
          <ui-button class="control-button prev-next" primary3 size="small"
            :disabled="!prevNextSession.next"
            @click="onNextClick">
            {{ $t('funnel.sessionReport.next') }}
          </ui-button>
          <span class="bar"></span>
          <span class="play-speed">
            <button v-for="speed in ['0.5', '1.0', '2.0']" :key="speed"
              :class="{ on: playSpeed === Number(speed) }"
              @click="playSpeed = Number(speed)">
              {{ speed }}x
            </button>
          </span>
          <span class="total">
            {{ $t('funnel.sessionReport.total') }} : {{ nodeItems.length }}
          </span>
        </div>
        <funnel-session-timeline
          :maxChartDuration="maxChartDuration"
          :timelineValuesY="timelineValuesY"
          :timelineSvgPathItems="timelineSvgPathItems"
          :activeIndex="activeIndex"
          :nodeItems="nodeItems"
          :eventItemsCssClassData="eventItemsCssClassData"
          :timelineItemWidth="timelineItemWidth"
          :timelineLeftPadding="timelineLeftPadding"
          @setActiveItem="setActiveItem"/>
        <button type="button" class="btn-timeline-toggle"
          @click="showTimeline = !showTimeline">
          <i class="icon icon--timeline-toggle"
            :class="{ up: !showTimeline }"></i>
        </button>
      </div>
    </div>
    <funnel-session-info
      :NODE_TYPE="NODE_TYPE"
      :sessionData="sessionData"
      :streamMeta="streamMeta"
      :nodeItems="nodeItems"
      :hoverIndex.sync="hoverIndex"
      :activeIndex="activeIndex"
      :timezoneName="timezoneName"
      :formatTime="formatTime"
      @setActiveItem="setActiveItem"/>
  </scroll-content>
</template>

<script>
import { parse, format } from 'date-fns'
import { getTimeParts } from '@/libs/utils'
import { getCurvedPathItems } from '@/libs/svg-helper'
import { getCaptureImageUrl } from '@/api/report'
import { getFunnelSession } from '@/api/funnel'
import AnimateScroll from '@/libs/animate-scroll'
import FunnelSessionDesktop from '@/components/funnels/funnel-session/FunnelSessionDesktop'
import FunnelSessionMobile from '@/components/funnels/funnel-session/FunnelSessionMobile'
import FunnelSessionTimeline from '@/components/funnels/funnel-session/FunnelSessionTimeline'
import FunnelSessionInfo from '@/components/funnels/funnel-session/FunnelSessionInfo'
import UiButton from '@/components/ui/UiButton'
import ScrollContent from '@/components/ui/ScrollContent'
import { mapState } from 'vuex'

function getItemType (item) {
  let type

  switch (item.type) {
    case 'a':
      type = 'active'
      break
    case 'i':
      type = 'inactive'
      break
    case 'hc':
      type = 'hoverClick'
      break
    case 'h':
      type = 'hover'
      break
    case 'c':
      type = 'click'
      break
  }

  if (item.n === 'scroll') {
    switch (item.scroll_type) {
      case 'Down':
        type = 'scrollDown'
        break
      case 'Up':
        type = 'scrollUp'
        break
    }
  }

  return type
}

const getDefaultData = () => {
  return {
    isReady: false,
    imageUrl: null,
    captureWidth: null,
    captureHeight: null,
    device: null,
    fitMode: 'width',
    rawData: null,
    sessionData: null,
    timelineSvgPathItems: [],
    timelineValuesY: [],
    timelineItemWidth: 30,
    timelineLeftPadding: 25,
    playSpeed: 1.0,
    hoverIndex: null,
    maxChartDuration: null,
    url: null,
    showTimeline: true,
    playState: 'ready',
    activeItem: null,
    isActionStart: false,
    sessionLoaderData: {
      isShow: false,
      isPlaying: false,
      r: 74 / 2,
      dashArray: Math.PI * 74,
      dashOffset: Math.PI * 74
    },
    timezoneName: '',
    activeNodeType: null,
    NODE_TYPE: {
      active: {
        name: 'Active',
        cssClass: {
          'node-active': true
        },
        isDisabled: true,
        isOff: false
      },
      inactive: {
        name: 'Inactive',
        cssClass: {
          'node-inactive': true
        },
        isDisabled: true,
        isOff: false
      },
      hover: {
        name: 'Hovering',
        cssClass: {
          'node-hover': true
        },
        isDisabled: true,
        isOff: false
      },
      click: {
        name: 'Click',
        cssClass: {
          'node-click': true
        },
        isDisabled: true,
        isOff: false
      },
      hoverClick: {
        name: 'Hover -> Click',
        cssClass: {
          'node-hover-click': true
        },
        isDisabled: true,
        isOff: false
      },
      scrollUp: {
        name: 'Scroll Up',
        cssClass: {
          'node-scroll-up': true
        },
        isDisabled: true,
        isOff: false
      },
      scrollDown: {
        name: 'Scroll Down',
        cssClass: {
          'node-scroll-down': true
        },
        isDisabled: true,
        isOff: false
      }
    }
  }
}

export default {
  name: 'FunnelSession',
  components: {
    UiButton,
    ScrollContent,
    FunnelSessionDesktop,
    FunnelSessionMobile,
    FunnelSessionTimeline,
    FunnelSessionInfo
  },
  data () {
    return {
      ...getDefaultData()
    }
  },
  computed: {
    ...mapState([
      'applyData'
    ]),
    hasData () {
      return this.rawData && this.nodeItems.length
    },
    isPhoneDevice () {
      return this.device === 'phone'
    },
    scale () {
      if (this.captureWidth && this.containerWidth) {
        return this.containerWidth / this.captureWidth
      }
      return 0
    },
    ratio () {
      if (this.captureWidth && this.captureHeight) {
        return this.captureWidth / this.captureHeight
      }
      return 0
    },
    containerWidth () {
      switch (this.device) {
        case 'desktop':
          return 700
        case 'phone':
          return 280
        default:
          return 0
      }
    },
    containerHeight () {
      return this.ratio ? this.containerWidth / this.ratio : 0
    },
    eventItems () {
      return this.sessionData ? this.sessionData.eventItems : []
    },
    nodeItems () {
      return this.eventItems.filter(item => !!item.nodeData)
    },
    hasPrevNode () {
      if (!this.nodeItems.length) {
        return false
      }

      return this.nodeItems[0].index !== this.activeIndex
    },
    hasNextNode () {
      if (!this.nodeItems.length) {
        return false
      }

      return this.nodeItems[this.nodeItems.length - 1].index !== this.activeIndex
    },
    eventItemsCssClassData () {
      const data = {}
      let activePrevNodeIndex = null
      let activeNextNodeIndex = null

      if (this.activeIndex !== null) {
        for (const item of this.nodeItems) {
          if (item.index < this.activeIndex) {
            activePrevNodeIndex = item.index
          }

          if (activeNextNodeIndex === null && item.index > this.activeIndex) {
            activeNextNodeIndex = item.index
          }
        }
      }

      for (const item of this.eventItems) {
        data[item.index] = {}

        if (this.NODE_TYPE[item.type]) {
          data[item.index] = { ...this.NODE_TYPE[item.type].cssClass }
        }

        const cssClass = data[item.index]

        if (this.hoverIndex === item.index) {
          cssClass.hover = true
        }

        if (this.activeIndex === item.index) {
          if (this.isActionStart) {
            cssClass.active = true
          } else {
            cssClass['active-initial'] = true
          }
        }

        let isActivePrev = false
        let isActiveNext = false

        if (this.activeIndex !== null) {
          if (item.index < this.activeIndex) {
            if (activePrevNodeIndex !== null) {
              if (item.index >= activePrevNodeIndex) {
                isActivePrev = true
              }
            } else {
              isActivePrev = true
            }
          } else if (item.index > this.activeIndex) {
            if (activeNextNodeIndex !== null) {
              if (item.index <= activeNextNodeIndex) {
                isActiveNext = true
              }
            } else {
              isActiveNext = true
            }
          }
        }

        cssClass['active-prev'] = isActivePrev
        cssClass['active-next'] = isActiveNext
        cssClass['phone'] = this.isPhoneDevice
      }
      return data
    },
    streamMeta () {
      if (this.rawData && this.rawData.streamMeta) {
        return this.rawData.streamMeta
      }
      return {}
    },
    activeIndex () {
      return this.activeItem ? this.activeItem.index : null
    },
    prevNextSession () {
      const data = {}

      if (!this.rawData || !this.rawData.list) {
        return data
      }

      if (this.rawData.list.prev && this.rawData.list.prev.aid) {
        data.prev = this.rawData.list.prev
      }

      if (this.rawData.list.next && this.rawData.list.next.aid) {
        data.next = this.rawData.list.next
      }

      return data
    }
  },
  watch: {
    $route (value) {
      Object.assign(this.$data, getDefaultData())
      this.$nextTick().then(this.loadSession)
    },
    activeItem (value) {
      if (!value) {
        return
      }
      const container = this.$refs.viewAreaBox.$el

      if (!container) {
        return
      }

      const containerPadding = 20
      const containerCenter = {
        left: container.offsetWidth / 2 - containerPadding,
        top: container.offsetHeight / 2 - containerPadding
      }

      let offsetTop

      if (this.isPhoneDevice) {
        offsetTop = value.y + 20

        if (value.height && value.height < 0) {
          offsetTop += value.height
        }
      } else {
        offsetTop = Math.max(0, value.y - containerCenter.top)
      }

      if (!this.scroller) {
        this.scroller = new AnimateScroll(container)
      }

      this.scroller.scrollTo(offsetTop, 300)
    }
  },
  created () {
    this.loadSession()
    document.documentElement.classList.add('funnel-session-html')
  },
  destroyed () {
    document.documentElement.classList.remove('funnel-session-html')
  },
  methods: {
    play () {
      this.isActionStart = true
      this.activeNodeType = null
      for (const key of Object.keys(this.NODE_TYPE)) {
        this.NODE_TYPE[key].isOff = false
      }

      if (this.playState === 'play') {
        this.pause()
        return
      } else if (this.activeItem && !this.activeItem.duration &&
        this.playState === 'ready' && !this.hasNextNode) {
        this.activeItem = null
      }

      this.playState = 'play'
      let ts = new Date().getTime()
      const fps = 30

      const playFn = () => {
        setTimeout(() => {
          if (this.playState !== 'play') {
            return
          }
          const now = new Date().getTime()
          const duration = (now - ts) * this.playSpeed
          ts = now

          if (!this.activeItem) {
            this.setActiveItem(this.nodeItems[0], false, true)
          }

          this.activeItem.duration = Math.max(0, this.activeItem.duration - duration)

          if (this.activeItem.duration > 0) {
            requestAnimationFrame(playFn)
          } else {
            const currentIndex = this.nodeItems.findIndex(item => {
              return item.index === this.activeItem.index
            })
            const nextItem = this.nodeItems[currentIndex + 1]

            if (nextItem) {
              this.playTimer = setTimeout(() => {
                ts = new Date().getTime()
                this.setActiveItem(nextItem, false, true)
                requestAnimationFrame(playFn)
              }, 500)
            } else {
              this.playState = 'ready'

              if (this.prevNextSession.next) {
                this.sessionLoaderTimer = setTimeout(() => {
                  this.openSessionLoader()
                }, 2000)
              }
            }
          }
        }, 1000 / fps)
      }
      requestAnimationFrame(playFn)
    },
    pause () {
      this.playState = 'pause'
      clearTimeout(this.playTimer)
    },
    onNextSessionPlay () {
      this.onNextClick()
    },
    onSessionLoaderTransitionEnd () {
      this.onNextClick()
    },
    onNextSessionCancel () {
      this.sessionLoaderData.isPlaying = false
      this.sessionLoaderData.dashOffset = this.sessionLoaderData.dashArray
      this.sessionLoaderData.isShow = false
    },
    onReplay () {
      this.onNextSessionCancel()
      this.play()
    },
    formatTime (ms, secFixedNum, format = 'm s') {
      if (!ms) {
        if (format === 'm s') {
          return '0'
        } else {
          return '00:00.000'
        }
      }

      const timeParts = getTimeParts(ms, secFixedNum)
      let hours = timeParts.h
      let minutes = timeParts.m
      let seconds = timeParts.s

      if (timeParts.d) {
        hours += timeParts.d * 24
      }

      if (hours) {
        minutes += hours * 60
      }

      let str

      if (format === 'm s') {
        str = `${seconds}s`

        if (minutes) {
          str = `${minutes}m ${str}`
        }
      } else {
        seconds = Number(seconds).toFixed(secFixedNum)

        if (seconds < 10) {
          seconds = `0${seconds}`
        }

        str = seconds

        if (minutes) {
          if (minutes < 10) {
            minutes = `0${minutes}`
          }
          str = `${minutes}:${seconds}`
        } else {
          str = `00:${seconds}`
        }
      }

      return str
    },
    getFitModeClass (mode) {
      return {
        on: this.fitMode === mode,
        fit_to_width: mode === 'width',
        fit_to_height: mode === 'height'
      }
    },
    onFitModeChange (mode) {
      this.fitMode = mode
    },
    async loadSession () {
      const query = this.$route.query
      const aid = query.aid
      const ssid = query.ssid
      const device = query.device
      this.device = device
      const startDate = query.startDate
      const endDate = query.endDate
      const uEndDate = query.uEndDate
      const timezoneName = query.timezoneName
      const seq = Number(query.seq)
      this.url = decodeURIComponent(query.url)

      getCaptureImageUrl({
        aid,
        device,
        startDate,
        endDate
      }, new Date().getTime()).then(imageUrl => {
        return this.initCaptureImageContainer(imageUrl)
      }).then(imageSize => {
        this.setImageContainer(imageSize)
        return getFunnelSession({
          ssid,
          aid,
          endDate,
          device,
          startDate,
          seq,
          uEndDate,
          timezoneName
        })
      }).then(data => {
        this.timezoneName = timezoneName
        this.rawData = data
        this.sessionData = this.getNormalizedData(data)
        this.$nextTick().then(() => {
          if (this.nodeItems.length) {
            this.setTimelineData()
            // this.setActiveItem(this.nodeItems[0], true)
          }
        })
      }).catch(err => {
        if (err.message === 'NO_FUNNEL_DATA') {
          // pass
        }
      })
    },
    initCaptureImageContainer (imageUrl) {
      return new Promise((resolve, reject) => {
        if (!imageUrl) {
          return reject(new Error('NO_IMAGE_URL'))
        }
        const img = document.createElement('img')
        img.addEventListener('load', event => {
          const el = event.target
          resolve({
            url: imageUrl,
            width: el.width,
            height: el.height
          })
        })
        img.addEventListener('error', () => {
          reject(new Error('INVALID_IMAGE'))
        })
        img.src = imageUrl
      })
    },
    setImageContainer (imageData) {
      this.captureWidth = imageData.width
      this.captureHeight = imageData.height
      this.imageUrl = imageData.url
    },
    getNormalizedData (rawData) {
      const eventItems = []
      let order = 0
      let maxDuration = 0
      let totalDuration = rawData.streamMeta.visit_time

      if (!rawData || !rawData.event || !rawData.event.length) {
        return {
          eventItems,
          maxDuration,
          totalDurationStr: this.formatTime(totalDuration, 1)
        }
      }

      for (let i = 0; i < rawData.event.length; i++) {
        const item = rawData.event[i]
        let x = item.rx || 0
        let y = item.ry || 0
        const minX = 0
        const maxX = this.captureWidth
        const minY = 0
        const maxY = this.captureHeight

        if (x < minX) {
          x = minX
        } else if (x > maxX) {
          x = maxX
        }

        if (y < minY) {
          y = minY
        } else if (y > maxY) {
          y = maxY
        }

        x *= this.scale
        y *= this.scale
        const index = i
        const type = getItemType(item)
        let nodeData = null
        let isNode = item.node && type

        if (this.isPhoneDevice) {
          isNode = isNode && ['scrollUp', 'scrollDown'].includes(type)
        }

        const duration = item.nodeDuration || item.duration

        if (isNode) {
          this.NODE_TYPE[type].isDisabled = false
          nodeData = {
            order: ++order,
            date: item.t,
            dateStr: format(parse(item.t), 'HH:mm:ss.SSS'),
            name: item.n
          }

          maxDuration = Math.max(maxDuration, duration)
        }

        eventItems.push({
          x,
          y,
          index,
          nodeData,
          type,
          duration: duration,
          scrollData: null
        })
      }

      if (!this.isPhoneDevice) {
        for (let i = 0; i < eventItems.length; i++) {
          const target = eventItems[i]
          const targetIndex = target.index
          const isScrollEvent = (type) => {
            return ['scrollUp', 'scrollDown'].includes(type)
          }

          if (target.nodeData && isScrollEvent(target.type)) {
            let nextPath

            for (let j = i + 1; j < eventItems.length; j++) {
              const nextEvent = eventItems[j]
              if (nextEvent.nodeData) {
                break
              }

              if (isScrollEvent(nextEvent.type)) {
                nextPath = nextEvent
              }
            }

            if (!nextPath) {
              nextPath = eventItems.find(item => item.nodeData && item.index > targetIndex)
            }

            if (!nextPath) {
              nextPath = eventItems[eventItems.length - 1]
            }

            if (nextPath) {
              eventItems.filter(item => item.index >= targetIndex && item.index < nextPath.index)
                .forEach(item => {
                  item.isHidden = true
                })

              target.scrollData = {
                dx: nextPath.x - target.x,
                dy: nextPath.y - target.y
              }
            }
          }
        }
      }

      const data = {
        eventItems,
        maxDuration,
        totalDurationStr: this.formatTime(totalDuration, 1)
      }
      return data
    },
    openSessionLoader () {
      this.sessionLoaderData.isShow = true
      setTimeout(() => {
        this.sessionLoaderData.isPlaying = true
      }, 100)
    },
    setTimelineData () {
      const points = []
      const maxDuration = this.sessionData.maxDuration
      const maxChartDuration = maxDuration * 1.4

      for (let i = 0; i < this.nodeItems.length; i++) {
        const item = this.nodeItems[i]

        points.push({
          x: i * 30 + 25,
          y: 100 - (item.duration / maxChartDuration * 100),
          index: item.index
        })
      }

      const items = getCurvedPathItems(points).map((item, index) => {
        return {
          ...item,
          index: points[index].index
        }
      })
      this.timelineSvgPathItems = items

      for (let i = 0; i < 5; i++) {
        const duration = i * 0.25 * maxChartDuration
        this.timelineValuesY.push(this.formatTime(duration, 3))
      }
      this.timelineValuesY.reverse()
      this.maxChartDuration = maxChartDuration
    },
    setActiveItem (item, isInitialAction, keepPlaying) {
      clearTimeout(this.sessionLoaderTimer)

      if (!keepPlaying) {
        this.pause()
      }

      this.isActionStart = !isInitialAction

      if (isInitialAction && this.isPhoneDevice) {
        return
      }

      if (this.activeNodeType) {
        this.onLegendItemClick(this.activeNodeType)
        for (const key of Object.keys(this.NODE_TYPE)) {
          this.NODE_TYPE[key].isOff = false
        }
      }

      if (this.activeItem && this.activeItem.index === item.index) {
        this.activeItem = null
        this.isActionStart = false
      } else {
        const tooltipLayerHeight = 160 * this.scale
        this.activeItem = {
          index: item.index,
          duration: this.isPhoneDevice ? 300 : item.duration,
          isTop: item.y > tooltipLayerHeight,
          x: item.x,
          y: item.y,
          height: item.height || null
        }
      }
    },
    onPverNextClick (isPrev) {
      const item = isPrev ? this.prevNextSession.prev : this.prevNextSession.next

      const query = {
        ...this.$route.query,
        aid: item.aid,
        url: encodeURIComponent(item.url),
        seq: item.seq,
        sid: item.sid
      }

      const to = {
        name: 'funnel.session',
        query
      }

      clearTimeout(this.playTimer)
      this.$router.push(to)
    },
    onPrevClick () {
      this.onPverNextClick(true)
    },
    onNextClick () {
      this.onPverNextClick(false)
    },
    onPrevItemClick () {
      if (this.activeItem) {
        const currentIndex = this.nodeItems.findIndex(item => item.index === this.activeItem.index)
        const prevItem = this.nodeItems[currentIndex - 1]

        if (prevItem) {
          this.setActiveItem(prevItem)
        }
      }
    },
    onNextItemClick () {
      if (!this.activeItem) {
        this.setActiveItem(this.nodeItems[0])
      } else {
        const currentIndex = this.nodeItems.findIndex(item => item.index === this.activeItem.index)
        const nextItem = this.nodeItems[currentIndex + 1]

        if (nextItem) {
          this.setActiveItem(nextItem)
        }
      }
    },
    onLegendItemClick (nodeType) {
      if (this.playState === 'play') {
        this.pause()
      }
      if (this.activeItem) {
        this.setActiveItem(this.nodeItems[this.activeItem.index], true)
      }
      if (this.activeNodeType === nodeType) {
        this.activeNodeType = null
      } else {
        this.activeNodeType = nodeType
      }

      for (const key of Object.keys(this.NODE_TYPE)) {
        this.NODE_TYPE[key].isOff = this.activeNodeType !== null && key !== nodeType
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.funnel-session {
  min-width: 1320px;
}

.data-pane {
  float: left;
  width: calc(100% - 410px);
  min-width: 870px;
  height: 100vh;
  overflow: hidden;
  background-color: #1b1b1d;

  .header {
    height: 40px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);

    .url {
      font-size: 15px;
      max-width: 800px;
      text-align: center;
      padding-top: 10px;
      margin: 0 auto;
      font-weight: 600;
      line-height: 1.4;
      color: #c1c1c1;
    }
  }

  .view-area-wrap {
    position: relative;
    height: calc(100vh - 41px - 235px);

    &.collapse {
      height: calc(100vh - 41px - 64px);

      .control-box {
        height: 64px;
      }
    }
  }

  .legend {
    position: absolute;
    top: 70px;
    left: 0;
    z-index: 10;

    &> li {
      width: 78px;
      height: 58px;
      text-align: center;

      &> .btn-legend {
        &[disabled] {
          opacity: 0.2;
        }
        &.off {
          opacity: 0.2;
        }
      }

      .icon {
        display: inline-block;
        box-sizing: border-box;
        width: 14px;
        height: 14px;
        line-height: 14px;
        border-radius: 50%;
        font-size: 11px;
        font-weight: 600;
      }

      .text {
        display: block;
        padding-top: 3px;
        font-size: 12px;
        line-height: 1.26;
        color: #c1c1c1;
      }

      &.node-active .icon,
      &.node-inactive .icon {
        border-radius: 3px;
        &> span {
          position: relative;
          top: 1px;
        }
      }

      &.node-active .icon {
        background-color: #fff;
        color: #444;
      }

      &.node-inactive .icon {
        border: solid 1px #b1b1b1;
        background-color: #1b1b1b;
        color: #fff;
      }
      &.node-hover .icon {
        background-color: #289eef;
      }
      &.node-click .icon {
        background-color: #ffd738;
      }
      &.node-hover-click .icon {
        background-color: #ab85ff;
      }

      &.node-scroll-up .icon,
      &.node-scroll-down .icon {
        background-color: #fff;
        text-align: left;
        &> span {
          display: inline-block;
          vertical-align: top;
          position: relative;
          top: 1px;
          left: 1px;
          width: 13px;
          height: 12px;
        }
      }
      &.node-scroll-up .icon span {
        background: url(../../../../assets/img/funnel/icon_arrow_up.svg) no-repeat 0 0;
      }
      &.node-scroll-down .icon span {
        background: url(../../../../assets/img/funnel/icon_arrow_down.svg) no-repeat 0 0;
      }
    }
  }

  .view-options {
    position: absolute;
    width: 20px;
    top: 69px;
    left: 32px;
    z-index: 10;

    .fit_to_width {
      display: block;
      margin-bottom: 6px;
    }
  }

  .control-box {
    position: relative;
    height: 235px;
    background-color: #fff;

    .control {
      box-sizing: border-box;
      height: 73px;
      padding-top: 15px;
      text-align: center;

      .control-button {
        margin: 0 5px;
        position: relative;
        &[disabled] {
          cursor: default;
        }

        &.prev-next {
          min-width: 70px;
          /deep/ .text {
            font-size: 13px;
            color: #444;
            font-weight: 600;
          }
        }
        .icon {
          position: relative;
          top: 1px;
          width: 16px;
          height: 16px;
          vertical-align: middle;
          background: transparent url(../../../../assets/img/funnel/icon_play.svg) no-repeat 0 0;
          background-size: 100% 100%;
          &.icon-pause {
            background-image: url(../../../../assets/img/funnel/icon_pause.svg);
          }
          &.icon-play-prev {
            background-image: url(../../../../assets/img/funnel/icon_play_rewind.svg);
          }
          &.icon-play-next {
            background-image: url(../../../../assets/img/funnel/icon_play_forward.svg);
          }
        }
      }

      .bar {
        margin: 0 10px 0 5px;
        display: inline-block;
        width: 1px;
        height: 12px;
        background-color: #d7d7d7;
      }

      .play-speed {
        display: inline-block;
        height: 27px;
        padding: 0;
        border-radius: 13.5px;
        border: solid 1px #e0e0e0;
        background-color: #ffffff;

        &> button {
          box-sizing: border-box;
          height: 27px;
          width: 47px;
          text-align: center;
          line-height: 27px;
          font-size: 13px;
          font-weight: 600;
          color: #888;

          &:hover {
            color: #444;
          }

          &.on {
            border-radius: 13.5px;
            box-shadow: 0 7px 10px -4px rgba(0, 0, 0, 0.3);
            background-color: #ec0047;
            color: #fff;
          }
        }
      }

      .total {
        margin-left: 12px;
        font-size: 12px;
        color: #666;
      }
    }

    .btn-timeline-toggle {
      position: absolute;
      right: 52px;
      top: -10px;
      width: 32px;
      height: 10px;
      border-radius: 5px 5px 0 0;
      background-color: #fff;
      overflow: visible;

      .icon--timeline-toggle {
        vertical-align: top;
        position: relative;
        top: 3px;
        width: 14px;
        height: 14px;
        background: url(../../../../assets/img/funnel/icon_timeline_down.svg) no-repeat 0 0;
        &.up {
          transform: rotate(180deg);
        }
      }
    }
  }
}

.session-loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  .session-loader-mask {
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    opacity: 0.8;
    background-color: #000;
    z-index: 100;
  }
  .session-loader-content {
    @include positionXYcenter;
    z-index: 200;
    color: #888;
    font-weight: 600;
    text-align: center;

    .url {
      font-size: 16px;
      max-width: 600px;
      margin-bottom: 12px;
    }
    .session-id {
      font-size: 30px;
      margin-bottom: 25px;
    }
    .next {
      font-size: 12px;
      font-weight: normal;
      color: #fff;
      margin-bottom: 10px;
    }
    .next-session-url {
      max-width: 600px;
      font-size: 16px;
      color: #fff;
      margin-bottom: 22px;
    }
    .loading {
      position: relative;
      margin: 0 auto 37px auto;
      width: 74px;
      height: 74px;

      .loading-svg {
        overflow: visible;

        .outer {
          fill: transparent;
          stroke: rgba(255, 255, 255, 0.15);
          stroke-width: 7;
        }
        .inner {
          fill: transparent;
          stroke: #fff;
          stroke-width: 7;
          stroke-linecap: round;
          transform: rotate(-90deg);
          transform-origin: center;
          &.play {
            transition: stroke-dashoffset 5s linear;
            stroke-dashoffset: 0 !important;
          }
        }
      }

      .btn-loading {
        width: 100%;
        height: 100%;
        @include positionXYcenter;
      }

      .icon.play-next {
        @include positionXYcenter;
        width: 26px;
        height: 26px;
        background-image: url(../../../../assets/img/funnel/icon_play_next_session.svg);
      }
    }
    .action {
      display: inline-block;
      padding: 25px 5px 0 5px;
      border-top: 1px solid rgba(255, 255, 255, 0.2);
      .btn-replay {
        margin-right: 5px;
      }
    }
  }
}
</style>
